/**
 * Web Push and PWA Add-to-Home-Screen utilities for Sakhtar Cloud ERP.
 */
import { apiGet, apiPost } from './apiClient.js';
import { tenantBasePath } from './tenant.js';

/**
 * Detects if the web app is running as an installed PWA (Add to Home Screen / Standalone).
 */
export function isPwaStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const isStandaloneMedia = window.matchMedia('(display-mode: standalone)').matches;
  const isIosStandalone =
    'standalone' in window.navigator &&
    Boolean((window.navigator as unknown as { standalone: boolean }).standalone);
  return isStandaloneMedia || isIosStandalone;
}

/**
 * Checks if the browser supports Service Worker, Push API, and Notifications.
 */
export function isPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Convert a base64url string to a Uint8Array for PushManager subscription.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Register the PWA service worker (/sw.js).
 */
export async function registerPwaServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (err) {
    console.error('Service worker registration failed:', err);
    return null;
  }
}

export interface PushStatus {
  readonly supported: boolean;
  readonly standalone: boolean;
  readonly permission: NotificationPermission | 'unsupported';
  readonly subscribed: boolean;
}

/**
 * Get current push notification status on this device.
 */
export async function getLocalPushStatus(): Promise<PushStatus> {
  if (!isPushSupported()) {
    return {
      supported: false,
      standalone: isPwaStandalone(),
      permission: 'unsupported',
      subscribed: false,
    };
  }

  const permission = Notification.permission;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;

  return {
    supported: true,
    standalone: isPwaStandalone(),
    permission,
    subscribed: sub !== null,
  };
}

/**
 * Request notification permission, subscribe to PushManager, and register with backend.
 */
export async function subscribeToPush(slug: string): Promise<boolean> {
  if (!isPushSupported()) {
    throw new Error('پوش نوتیفیکیشن در این مرورگر یا دستگاه پشتیبانی نمی‌شود.');
  }

  // 1. Ensure service worker is registered
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = (await registerPwaServiceWorker()) ?? undefined;
  }
  if (!reg) {
    throw new Error('سرویس ورکر فعال نشد.');
  }

  // 2. Fetch VAPID public key & check if push notifications are enabled on the server
  const base = tenantBasePath(slug);
  const resp = await apiGet<{ publicKey: string | null; enabled?: boolean }>(
    `${base}/api/v1/notifications/push/vapid-public-key`,
  );

  if (resp.enabled === false || !resp.publicKey) {
    return false;
  }
  const publicKey = resp.publicKey;

  // 3. Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return false;
  }

  // 4. Subscribe via PushManager
  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey as unknown as BufferSource,
  });

  const subJson = subscription.toJSON();
  if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) {
    throw new Error('اشتراک نامعتبر است.');
  }

  // 5. Send subscription to server
  await apiPost(`${base}/api/v1/notifications/push/subscribe`, {
    endpoint: subJson.endpoint,
    keys: {
      p256dh: subJson.keys.p256dh,
      auth: subJson.keys.auth,
    },
    userAgent: navigator.userAgent,
    isStandalone: isPwaStandalone(),
  });

  return true;
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(slug: string): Promise<boolean> {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;

  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;

  const endpoint = sub.endpoint;
  await sub.unsubscribe();

  const base = tenantBasePath(slug);
  await apiPost(`${base}/api/v1/notifications/push/unsubscribe`, { endpoint }).catch(() => {});
  return true;
}

/**
 * Send an immediate actor-centric test push notification to this device.
 */
export async function sendTestPushNotification(slug: string): Promise<{
  success: boolean;
  deliveredToDevices: number;
}> {
  const base = tenantBasePath(slug);
  return await apiPost(`${base}/api/v1/notifications/push/test`);
}
